import { Delete, FileDownload } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { getCompanyMultipleConfig } from 'lib/api/company';
import {
  fetchFactoringExtension,
  useGetFactoringExtension,
  usePreviewDeleteFactoringExtension,
  useUpdateFactoringExtension,
} from 'lib/api/extension/factoring';
import type { GetFactoringExtensionPendingAccountDetail } from 'lib/api/extension/factoring/types';
import WarningDeleteDialog from 'lib/common/WarningDeleteDialog';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import PageHeader, { type ActionButton } from 'lib/designSystem/PageHeader';
import ProcessDialog, {
  type ProcessDialogTab,
} from 'lib/designSystem/ProcessDialog';
import {
  formatDate,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import { ExtensionExportDialog } from 'pages/Finance/Components/Financing/common/ExtensionExportDialog/components/ExtensionExportDialog';
import { useExtensionCheckboxes } from 'pages/Finance/Components/Financing/common/ExtensionExportDialog/hooks/useExtensionCheckboxes';
import React, { lazy, Suspense, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import type { DocumentDebtStatus } from 'services/redux/documentProfileSlice.types';

import { ExtensionFormContext } from './context';
import { isExtensionInReview } from './utils';

const FactoringExtensionMissingApprovalContainer = lazy(
  () => import('./components/Risk'),
);
const FactoringExtensionOperationsApprovalContainer = lazy(
  () => import('./components/Operations'),
);
const SummaryTab = lazy(() => import('./components/Summary'));
const PendingAccountsTab = lazy(() => import('./components/PendingAccounts'));

interface ExtensionDetailsProps {
  extension: { status?: string } & Record<string, unknown>;
  setExtension: (
    extension: { status?: string } & Record<string, unknown>,
  ) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSuccess?: (message: string) => void;
  setUp?: () => Promise<void> | void;
  setFailure?: (message: string) => void;
  edit: boolean;
  onExportExtension?: (
    extension: { status?: string } & Record<string, unknown>,
  ) => void;
  onDeleteExtension?: (
    extension: { status?: string } & Record<string, unknown>,
  ) => void;
}

const ExtensionDetails: React.FC<ExtensionDetailsProps> = (props) => {
  const {
    extension,
    setExtension,
    open,
    setOpen,
    setSuccess,
    setUp,
    setFailure,
    edit,
    onExportExtension,
    onDeleteExtension,
  } = props;

  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const queryClient = useQueryClient();

  const [startValue] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [extensionCheckboxes, extensionCheckboxesSetters] =
    useExtensionCheckboxes();

  const [extensionState, setExtensionState] = useState(extension);
  const [documentState, setDocumentState] = useState<DocumentDebtStatus | null>(
    null,
  );
  const [canModifyParams, setCanModifyParams] = useState(false);
  const [searched, setSearched] = useState(true);
  const [canChangeArrearRate, setCanChangeArrearRate] = useState(false);
  const [noIvaComision, setNoIvaComision] = useState<number | null>(null);
  const [requiresRiskApproval, setRequiresRiskApproval] = useState(false);
  const [requiresOperationsApproval, setRequiresOperationsApproval] =
    useState(false);
  const [pendingAccounts, setPendingAccounts] = useState<
    GetFactoringExtensionPendingAccountDetail[]
  >([]);
  const [canEditExtension, setCanEditExtension] = useState(false);

  const newExtension = !extension.status;

  const title = `Prórroga #${extensionState.id}`;

  const extensionId = extension.id as string | number | null | undefined;

  const previewDeleteEnabled =
    openDeleteConfirm &&
    onDeleteExtension != null &&
    extensionId != null &&
    String(extensionId) !== '';

  const previewDeleteQuery = usePreviewDeleteFactoringExtension(
    dispatch,
    String(extensionId ?? ''),
    previewDeleteEnabled,
  );

  const { data, isFetching } = useGetFactoringExtension(
    dispatch,
    String(extensionId ?? ''),
    extensionId != null,
  );

  const updateMutation = useUpdateFactoringExtension(
    dispatch,
    extensionId as string | number,
  );

  React.useEffect(() => {
    if (!data) return;
    setExtensionState({
      ...data.extension,
      dueDate: formatDate(
        new Date(data.extension.dueDate as string),
        'yyyy-mm-dd',
      ),
    });
    setDocumentState(data.document);
    setPendingAccounts(data.pendingAccounts ?? []);
    setCanEditExtension(data.canEdit === true);
    setSearched(true);
  }, [data]);

  React.useEffect(() => {
    if (!canEditExtension) {
      setCanModifyParams(false);
    }
  }, [canEditExtension]);

  React.useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await getCompanyMultipleConfig(dispatch, [
          'fixed-arrear-rate-factoringExtension',
          'factoringExtension-no-iva-comision',
          'factoring_extension_requires_risk_approval',
          'factoring_extension_requires_operations_approval',
        ]);
        const noIva = response['factoringExtension-no-iva-comision']
          ? parseFloat(response['factoringExtension-no-iva-comision'])
          : null;
        setNoIvaComision(noIva);
        const canChange = validatePermissions(
          user,
          'factoring_request_edit_arrear_rate',
        );
        setCanChangeArrearRate(canChange);

        setRequiresRiskApproval(
          response['factoring_extension_requires_risk_approval'] === '1',
        );
        setRequiresOperationsApproval(
          response['factoring_extension_requires_operations_approval'] === '1',
        );
      } catch (error) {
        console.error('Error al cargar configs de ExtensionDetails', error);
      }
    };
    void loadConfig();
  }, [dispatch, user]);

  const submitExtension = async () => {
    try {
      await updateMutation.mutateAsync({
        rate:
          typeof extensionState.rate === 'number'
            ? extensionState.rate
            : extensionState.rate
              ? Number(extensionState.rate)
              : undefined,
        rateCost: extensionState.rateCost as number | undefined,
        operationCostFixed: extensionState.operationCostFixed as
          | number
          | undefined,
        operationCostVariable: extensionState.operationCostVariable as
          | number
          | undefined,
        operationCostRateVariable:
          typeof extensionState.operationCostRateVariable === 'number'
            ? extensionState.operationCostRateVariable
            : extensionState.operationCostRateVariable
              ? Number(extensionState.operationCostRateVariable)
              : undefined,
        startDate: extensionState.startDate
          ? new Date(extensionState.startDate as string)
          : undefined,
        dueDate: new Date(extensionState.dueDate as string),
        observation:
          extensionState.observation !== undefined
            ? (extensionState.observation as string)
            : undefined,
      });
      showAlert(dispatch, 'success', 'Prórroga actualizada correctamente');
      const updated = await fetchFactoringExtension(
        dispatch,
        extensionId as number | string,
      );
      if (updated) {
        setExtensionState({
          ...updated.extension,
          dueDate: formatDate(
            new Date(updated.extension.dueDate as string),
            'yyyy-mm-dd',
          ),
        });
        setDocumentState(updated.document);
        setPendingAccounts(updated.pendingAccounts ?? []);
        setCanEditExtension(updated.canEdit === true);
        setExtension(updated.extension);
        await queryClient.invalidateQueries({
          queryKey: ['factoringExtensions'],
        });
      }
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al actualizar la prórroga');
      throw error;
    }
  };

  const showPendingAccountsTab =
    Boolean(extensionState.status) &&
    !isExtensionInReview(extensionState.status);

  const tabs: ProcessDialogTab[] = [
    {
      label: 'Resumen',
      content: (
        <Suspense fallback={<CenteredCircularProgress type="dialog" />}>
          <SummaryTab />
        </Suspense>
      ),
    },
    ...(showPendingAccountsTab
      ? [
          {
            label: 'Cuentas',
            content: (
              <Suspense fallback={<CenteredCircularProgress type="dialog" />}>
                <PendingAccountsTab />
              </Suspense>
            ),
          } as ProcessDialogTab,
        ]
      : []),
    ...(requiresRiskApproval
      ? [
          {
            label: 'Riesgo',
            disabled: newExtension,
            content: (
              <Suspense fallback={<CenteredCircularProgress type="dialog" />}>
                <FactoringExtensionMissingApprovalContainer
                  extension={extensionState}
                  setOpen={setOpen}
                  setSuccess={setSuccess}
                  setUp={setUp}
                  setFailure={setFailure}
                  edit={edit}
                />
              </Suspense>
            ),
          } as ProcessDialogTab,
        ]
      : []),
    ...(requiresOperationsApproval
      ? [
          {
            label: 'Operaciones',
            disabled: newExtension,
            content: (
              <Suspense fallback={<CenteredCircularProgress type="dialog" />}>
                <FactoringExtensionOperationsApprovalContainer
                  extension={extensionState}
                  setOpen={setOpen}
                  setSuccess={setSuccess}
                  setUp={setUp}
                  setFailure={setFailure}
                  edit={edit}
                />
              </Suspense>
            ),
          } as ProcessDialogTab,
        ]
      : []),
  ];

  const headerActions: ActionButton[] = [
    {
      label: 'Exportar',
      onClick: () => {
        if (onExportExtension) {
          onExportExtension(extension);
        } else {
          setExportDialogOpen(true);
        }
      },
      color: 'primary',
      startIcon: <FileDownload />,
    },
    ...(onDeleteExtension && !newExtension
      ? [
          {
            label: 'Eliminar',
            onClick: () => setOpenDeleteConfirm(true),
            color: 'error',
            startIcon: <Delete />,
          } as ActionButton,
        ]
      : []),
  ];

  const tabsWithLoading: ProcessDialogTab[] =
    isFetching && documentState == null
      ? [
          {
            label: 'Resumen',
            content: <CenteredCircularProgress type="dialog" />,
          },
        ]
      : tabs;

  return (
    <ExtensionFormContext.Provider
      value={{
        extension: extensionState,
        setExtension: setExtensionState,
        document: documentState,
        setDocument: setDocumentState,
        searched,
        setSearched,
        canModifyParams,
        setCanModifyParams,
        canChangeArrearRate,
        noIvaComision,
        edit,
        canEdit: canEditExtension,
        submitExtension,
        pendingAccounts,
      }}
    >
      <>
        <ProcessDialog.WithTabsHeader
          open={open}
          onClose={() => setOpen(false)}
          title={
            <PageHeader.TitleSection
              title={title}
              iconType="extension"
              iconVariant="outlined"
              titleVariant="large"
            />
          }
          status={extensionState.status}
          tabs={tabsWithLoading}
          initialIndex={startValue}
          contentVariant="comfortable"
          headerActions={headerActions}
        />
        <ExtensionExportDialog
          open={exportDialogOpen}
          setOpen={setExportDialogOpen}
          extension={extensionState}
          checkboxes={extensionCheckboxes}
          setters={extensionCheckboxesSetters}
          document={documentState as Record<string, unknown> | null}
          pendingAccounts={pendingAccounts}
        />
        <WarningDeleteDialog
          open={openDeleteConfirm}
          setOpen={setOpenDeleteConfirm}
          title={`Eliminar prórroga #${extensionState.id}`}
          subtitle={
            previewDeleteQuery.data?.canDelete === false
              ? 'Lamentamos no se puede eliminar esta prórroga'
              : undefined
          }
          warningMessages={
            previewDeleteQuery.isError
              ? [
                  'No se pudo verificar si la prórroga puede eliminarse. Inténtalo de nuevo.',
                ]
              : (previewDeleteQuery.data?.warningMessages ?? [])
          }
          previewLoading={
            previewDeleteEnabled &&
            (previewDeleteQuery.isPending || previewDeleteQuery.isFetching)
          }
          allowDelete={
            !previewDeleteQuery.isError &&
            previewDeleteQuery.data?.canDelete === true
          }
          requireConfirmation={previewDeleteQuery.data?.canDelete !== false}
          mode={
            previewDeleteQuery.data?.canDelete === false ? 'info' : 'action'
          }
          deleteItem={async () => {
            if (!onDeleteExtension) return;
            await Promise.resolve(onDeleteExtension(extension));
          }}
          actionType="delete"
          severity={
            previewDeleteQuery.data?.canDelete === false ? 'info' : 'warning'
          }
        />
      </>
    </ExtensionFormContext.Provider>
  );
};

export default ExtensionDetails;
